import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageIcon, UploadIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import { useRole } from '@/hooks/use-role';
import {
  businessQueryKey,
  getBusiness,
  updateBusiness,
} from '@/lib/api/businesses';
import { ACCEPTED_LOGO_TYPES } from '@/lib/constants';
import { uploadFile } from '@/lib/storage';

// Owner-only logo uploader for the business page. The upload runs inside the
// mutation's `mutationFn` (presigned-URL upload → then `PATCH /businesses`),
// so `isPending` covers the whole round trip.
export function BusinessLogoSection() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;
  const { isOwner } = useRole();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const businessQuery = useQuery({
    queryKey: businessQueryKey(businessId ?? ''),
    queryFn: () => getBusiness(businessId!),
    enabled: Boolean(businessId) && isOwner,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const { url } = await uploadFile({
        businessId: businessId!,
        file,
        kind: 'logo',
      });
      return updateBusiness(businessId!, { logo_url: url });
    },
    meta: { successMessage: t.businessLogoUpdated },
    onSuccess: (updated) => {
      queryClient.setQueryData(businessQueryKey(businessId ?? ''), updated);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  if (!isOwner) return null;

  const logoUrl = businessQuery.data?.logo_url;

  return (
    <section
      className="flex flex-col gap-3 rounded-xl border bg-card p-5"
      data-testid="business-logo"
    >
      <div>
        <h2 className="text-sm font-semibold">{t.businessLogoTitle}</h2>
        <p className="text-xs text-muted-foreground">
          {t.businessLogoSubtitle}
        </p>
      </div>

      <div className="flex items-center gap-4 border-t pt-4">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={t.businessLogoAlt}
            data-testid="business-logo-image"
            className="w-16 h-16 rounded-lg object-contain border bg-background"
          />
        ) : (
          <div className="w-16 h-16 rounded-lg border flex items-center justify-center bg-muted text-muted-foreground">
            <ImageIcon className="w-6 h-6" />
          </div>
        )}

        <div className="flex flex-col gap-2">
          {!logoUrl && (
            <p className="text-xs text-muted-foreground">
              {t.businessLogoNone}
            </p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_LOGO_TYPES.join(',')}
            data-testid="business-logo-file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadMutation.mutate(file);
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="self-start gap-1.5"
            disabled={uploadMutation.isPending}
            data-testid="business-logo-upload"
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadIcon className="w-3.5 h-3.5" />
            {uploadMutation.isPending
              ? t.businessLogoUploading
              : t.businessLogoUpload}
          </Button>
        </div>
      </div>
    </section>
  );
}
